// ts-rest contract for the postcode address lookup (SEP-243 section 9,
// decision D5).
//
// The firm asked for a postcode lookup on the fact-find address block, with a
// manual fallback for an address the service does not hold. Decision D5 adds
// two rules that shape this contract:
//
//   1. The API key stays on the SERVER. The browser never holds it, and the
//      browser never calls the vendor. Every lookup goes through this proxy.
//   2. The firm supplies the key later, so the KEYLESS path is the one that
//      ships first. `getAvailability` therefore exists as its own read: the
//      form asks once whether a lookup is possible, and it renders the manual
//      address fields either way. A lookup with no key refuses with 503 and the
//      code `lookup_not_configured`. It NEVER invents an address, which follows
//      the same rule the AML vendor door holds (ADR-0009, ADR-0020).
//
// Endpoints under /api/address/*:
//   GET /api/address/availability   - can this deployment look a postcode up?
//   GET /api/address/lookup         - the addresses at one postcode

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorBody = z.object({ error: z.string(), code: z.string().optional() });

/**
 * One address the lookup returned, in the shape the fact-find address block
 * fills. The vendor answers with its own field names; the server maps them
 * onto these, so a change of vendor never reaches the form.
 */
export const addressLookupResultSchema = z.object({
    /** A stable key for the list. It is the vendor's own id where it gives one. */
    id: z.string(),
    /** The one-line address, for the picker row. */
    label: z.string(),
    line1: z.string(),
    line2: z.string(),
    line3: z.string(),
    city: z.string(),
    county: z.string(),
    postCode: z.string(),
    /**
     * True when the row is a SUGGESTION and not an address yet.
     *
     * The keyed vendor answers `/autocomplete` with a label and an id, and it
     * answers the lines only when the caller asks for one row by that id. The
     * form therefore resolves the row the client picks, and only that row.
     */
    resolvable: z.boolean().default(false),
});
export type AddressLookupResult = z.infer<typeof addressLookupResultSchema>;

export const addressLookupAvailabilitySchema = z.object({
    /**
     * True when this deployment can look a postcode up at all. It is true for
     * the keyless open-data source as well, which answers the town, the county
     * and the country but no building.
     */
    configured: z.boolean(),
    /** The vendor name, for the operator. Null when no lookup is possible. */
    vendor: z.string().nullable(),
    /**
     * How much of the address the source gives.
     *
     * `address` is a building-level source: it lists each address at the
     * postcode and it fills every line. `postcode` is the open-data source: it
     * gives the town, the county and the country, and the client types the
     * house number and the street. The form tells the client which one it has,
     * because a button that promises an address must give one.
     */
    precision: z.enum(['address', 'postcode']).nullable(),
    /**
     * True when the source takes FREE TEXT.
     *
     * A geocoder is a type-ahead: it reads "14 Fenwick" and answers the
     * buildings that match, and it cannot list every address at a postcode. A
     * postal-file source is the other way about. The form draws the control
     * the source can actually serve: a search box, or a button beside the
     * postcode field.
     */
    freeText: z.boolean().default(false),
});
export type AddressLookupAvailability = z.infer<typeof addressLookupAvailabilitySchema>;

export const addressLookupResponseSchema = z.object({
    /** The postcode as the vendor normalised it. */
    postCode: z.string(),
    /** The addresses at the postcode. An empty list is a valid answer. */
    addresses: z.array(addressLookupResultSchema),
});
export type AddressLookupResponse = z.infer<typeof addressLookupResponseSchema>;

export const addressLookupContract = c.router(
    {
        getAvailability: {
            method: 'GET',
            path: '/address/availability',
            responses: { 200: addressLookupAvailabilitySchema },
            summary: 'Whether this deployment can look an address up by postcode',
        },
        search: {
            method: 'GET',
            path: '/address/search',
            query: z.object({ q: z.string() }),
            responses: {
                200: addressLookupResponseSchema,
                400: errorBody,
                502: errorBody,
                503: errorBody,
            },
            summary: 'The addresses that match a free-text term, for a type-ahead',
        },
        resolve: {
            method: 'GET',
            path: '/address/resolve',
            query: z.object({ id: z.string() }),
            responses: {
                200: addressLookupResultSchema,
                400: errorBody,
                404: errorBody,
                502: errorBody,
                503: errorBody,
            },
            summary: 'The lines of one suggestion, by the id the lookup gave',
        },
        lookup: {
            method: 'GET',
            path: '/address/lookup',
            query: z.object({ postcode: z.string() }),
            responses: {
                200: addressLookupResponseSchema,
                // 400 = the postcode is not a UK postcode shape.
                400: errorBody,
                // 502 = the vendor answered, and the answer was not usable.
                502: errorBody,
                // 503 = this deployment holds no vendor key. The form falls back
                // to manual entry, which is always available.
                503: errorBody,
            },
            summary: 'The addresses at one UK postcode, through the server-side vendor proxy',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
